"use client";

import React, { useEffect, useState } from "react";
import { Terminal, ShieldCheck, Clock, Activity, Lock } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
function LiveClock() {
  const [time, setTime] = useState<Date | null>(null);
  useEffect(() => {
    setTime(new Date());
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  if (!time) {
    return <div data-section-id="223533" className="font-mono text-4xl font-light tracking-widest text-primary/50">
        00:00:00
      </div>;
  }
  return <div className="flex flex-col gap-1">
      <div className="font-mono text-4xl md:text-5xl font-light tracking-widest text-primary drop-shadow-[0_0_12px_rgba(0,255,255,0.6)]">
        {time.toLocaleTimeString("en-US", {
        hour12: false
      })}
      </div>
      <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-widest">
        <Activity className="size-3 text-primary animate-pulse" />
        Neural Processing Time
      </div>
    </div>;
}
function TechnicalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [authCode, setAuthCode] = useState("");
  const isUnlocked = authCode.length === 4;
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 border border-primary/30 bg-primary/5 p-8 rounded-lg">
        <ShieldCheck className="size-12 text-primary" />
        <h3 className="font-default text-xl text-primary uppercase tracking-widest font-bold">
          Sequence Initiated
        </h3>
        <p className="font-mono text-sm text-muted-foreground">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      {!isUnlocked ? <div className="flex flex-col items-center justify-center py-16 space-y-8 border border-border/20 bg-card/30 backdrop-blur-md p-8 rounded-xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,255,255,0.03)_50%)] bg-[length:100%_4px] pointer-events-none" />
          
          <div className="flex flex-col items-center text-center space-y-2 z-10">
            <Lock className="size-8 text-muted-foreground mb-2" />
            <h3 className="font-default text-lg text-foreground uppercase tracking-widest font-semibold">
              Production Access Required
            </h3>
            <p className="font-mono text-xs text-muted-foreground">
              Enter 4-digit authorization code to access the AI creative suite.
            </p>
          </div>

          <div className="z-10">
            <InputOTP maxLength={4} value={authCode} onChange={setAuthCode} containerClassName="gap-2">
              <InputOTPGroup>
                <InputOTPSlot index={0} className="font-mono text-lg border-primary/40 text-primary rounded-md" />
                <InputOTPSlot index={1} className="font-mono text-lg border-primary/40 text-primary rounded-md" />
                <InputOTPSlot index={2} className="font-mono text-lg border-primary/40 text-primary rounded-md" />
                <InputOTPSlot index={3} className="font-mono text-lg border-primary/40 text-primary rounded-md" />
              </InputOTPGroup>
            </InputOTP>
          </div>
          
          <div className="text-[10px] font-mono text-muted-foreground/50 uppercase tracking-widest z-10">
            [ Simulation Mode: Any 4 Digits ]
          </div>
        </div> : <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="flex items-center justify-between border-b border-primary/20 pb-4">
            <div className="flex items-center gap-2">
              <Terminal className="size-5 text-primary" />
              <h3 className="font-default text-sm text-primary uppercase tracking-widest font-bold">
                Commercial Specs
              </h3>
            </div>
            <div className="font-mono text-[10px] text-primary/70 uppercase">
              Status: Verified
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="name" rules={{
          required: "Identification required"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Project Lead *
                  </FormLabel>
                  <FormControl>
                    <input {...field} className="flex h-10 w-full rounded-md border border-border/40 bg-background/40 px-3 py-2 text-sm font-mono text-foreground ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" placeholder="Alex Rivera" />
                  </FormControl>
                  <FormDescription className="font-mono text-[10px]">Primary contact for production.</FormDescription>
                  <FormMessage className="font-mono text-xs" />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
          required: "Comms channel required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid comms format"
          }
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Contact Email *
                  </FormLabel>
                  <FormControl>
                    <input {...field} type="email" className="flex h-10 w-full rounded-md border border-border/40 bg-background/40 px-3 py-2 text-sm font-mono text-foreground ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" placeholder="alex@brand.ai" />
                  </FormControl>
                  <FormDescription className="font-mono text-[10px]">Secure channel for asset delivery.</FormDescription>
                  <FormMessage className="font-mono text-xs" />
                </FormItem>} />

            <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Brand Name
                  </FormLabel>
                  <FormControl>
                    <input {...field} className="flex h-10 w-full rounded-md border border-border/40 bg-background/40 px-3 py-2 text-sm font-mono text-foreground ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" placeholder="Innovate Corp" />
                  </FormControl>
                  <FormMessage className="font-mono text-xs" />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[0-9+\-\s()]*$/,
            message: "Invalid frequency format"
          }
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Direct Line
                  </FormLabel>
                  <FormControl>
                    <input {...field} type="tel" className="flex h-10 w-full rounded-md border border-border/40 bg-background/40 px-3 py-2 text-sm font-mono text-foreground ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" placeholder="+1 800-AI-VIDEO" />
                  </FormControl>
                  <FormMessage className="font-mono text-xs" />
                </FormItem>} />
          </div>

          <FormField control={control} name="topic" rules={{
        required: "Objective required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Campaign Type *
                </FormLabel>
                <FormControl>
                  <input {...field} className="flex h-10 w-full rounded-md border border-border/40 bg-background/40 px-3 py-2 text-sm font-mono text-foreground ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" placeholder="TikTok Ads, Instagram Reels, Product Launch" />
                </FormControl>
                <FormMessage className="font-mono text-xs" />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
        required: "Details required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Creative Vision *
                </FormLabel>
                <FormControl>
                  <textarea {...field} className="flex min-h-[120px] w-full rounded-md border border-border/40 bg-background/40 px-3 py-2 text-sm font-mono text-foreground ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300 resize-none" placeholder="Describe the aesthetic, goals, and core message for your AI-powered commercial..." />
                </FormControl>
                <FormMessage className="font-mono text-xs" />
              </FormItem>} />

          {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 text-destructive font-mono text-sm uppercase tracking-wider rounded-md">
              Error: {submitError}
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full rounded-md font-default font-bold uppercase tracking-widest h-12 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 relative overflow-hidden group shadow-lg shadow-primary/20">
            <span className="relative z-10 flex items-center gap-2">
              {isSubmitting ? <>
                  <Activity className="size-4 animate-spin" />
                  Generating Commercial...
                </> : <>
                  <Terminal className="size-4" />
                  Initiate Production
                </>}
            </span>
            <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
          </Button>
        </div>}
    </Form>;
}
export default function TheBriefingRoom() {
  return <section id="launch-your-commercial" className="relative bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-primary)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_20%,transparent_100%)]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          
          {}
          <div className="lg:col-span-5 flex flex-col justify-between space-y-12">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-primary/40 bg-primary/10 text-primary font-mono text-xs uppercase tracking-widest rounded-full">
                <Clock className="size-3" />
                <span>AI Creative Studio</span>
              </div>
              
              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-foreground">
                The AI <br className="hidden lg:block" />
                <span className="text-primary drop-shadow-[0_0_15px_rgba(0,255,255,0.3)]">Studio</span>
              </h2>
              
              <p className="font-default text-lg text-muted-foreground max-w-md leading-relaxed">
                Connect your brand to our neural production network. 
                We leverage hyper-advanced AI to craft social commercials 
                that disrupt feeds and dominate attention.
              </p>
            </div>

            <div className="p-8 border border-border/20 bg-card/40 backdrop-blur-xl rounded-2xl space-y-8 relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-primary to-secondary" />
              
              <LiveClock />

              <div className="space-y-4 pt-8 border-t border-border/20">
                <div className="flex justify-between items-center font-mono text-xs">
                  <span className="text-muted-foreground uppercase tracking-widest">Neural Status</span>
                  <span className="text-primary flex items-center gap-2 font-bold">
                    <span className="size-2 rounded-full bg-primary animate-pulse" />
                    SYNCHRONIZED
                  </span>
                </div>
                <div className="flex justify-between items-center font-mono text-xs">
                  <span className="text-muted-foreground uppercase tracking-widest">AI Engine</span>
                  <span className="text-foreground">GPT-4V/CLIP-64</span>
                </div>
                <div className="flex justify-between items-center font-mono text-xs">
                  <span className="text-muted-foreground uppercase tracking-widest">Render Latency</span>
                  <span className="text-foreground">0.02ms</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 lg:pl-12">
            <div className="border border-border/20 bg-card/20 backdrop-blur-2xl p-6 md:p-10 rounded-2xl shadow-3xl shadow-primary/10 relative">
              {}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/60 -translate-x-2 -translate-y-2 rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary/60 translate-x-2 -translate-y-2 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary/60 -translate-x-2 translate-y-2 rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary/60 translate-x-2 translate-y-2 rounded-br-lg" />

              <div className="mb-8 border-b border-border/20 pb-6">
                <h3 className="font-default text-3xl font-bold text-foreground tracking-tight">
                  Launch Your Commercial
                </h3>
                <p className="font-mono text-[10px] text-primary/70 mt-2 uppercase tracking-widest font-bold">
                  Ref ID: AIC-PROD-2024
                </p>
              </div>

              <FormProvider formKey="launch_commercial_form_home" formId="launch_commercial_form_home" sectionName="the-briefing-room" defaultValues={{
              name: "",
              email: "",
              company: "",
              phone: "",
              topic: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              topic: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Initiate Production" successMessage="Creative parameters logged. Our AI specialists will contact you within 24 hours to begin the generation process.">
                <TechnicalForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}